// src/pages/portal/PortalNuevoTicketPage.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCrearPortalTicket, usePortalEmpresas } from '@/hooks/queries';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Label,
} from '@/components/ui';
import { ArrowLeft } from 'lucide-react';

export default function PortalNuevoTicketPage() {
  const navigate = useNavigate();
  const { data: empresas } = usePortalEmpresas();
  const crearTicket = useCrearPortalTicket();

  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    tipo: 'consulta',
    prioridad: 'media',
    empresa_id: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    crearTicket.mutate(
      {
        titulo: formData.titulo,
        descripcion: formData.descripcion || undefined,
        tipo: formData.tipo || undefined,
        prioridad: formData.prioridad || undefined,
        empresa_id: formData.empresa_id || undefined,
      },
      {
        onSuccess: (ticket) => {
          navigate(`/portal/tickets/${ticket.id}`);
        },
      }
    );
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/portal/tickets')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Crear Nuevo Ticket</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Titulo */}
            <div>
              <Label htmlFor="titulo">Titulo *</Label>
              <Input
                id="titulo"
                value={formData.titulo}
                onChange={(e) =>
                  setFormData({ ...formData, titulo: e.target.value })
                }
                placeholder="Describe brevemente tu problema"
                required
              />
            </div>

            {/* Descripción */}
            <div>
              <Label htmlFor="descripcion">Descripción</Label>
              <Textarea
                id="descripcion"
                value={formData.descripcion}
                onChange={(e) =>
                  setFormData({ ...formData, descripcion: e.target.value })
                }
                placeholder="Proporciona mas detalles sobre tu solicitud"
                rows={5}
              />
            </div>

            {/* Tipo y Prioridad */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="tipo">Tipo</Label>
                <Select
                  value={formData.tipo}
                  onValueChange={(value) => setFormData({ ...formData, tipo: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="consulta">Consulta</SelectItem>
                    <SelectItem value="incidencia">Incidencia</SelectItem>
                    <SelectItem value="solicitud">Solicitud</SelectItem>
                    <SelectItem value="mejora">Sugerencia de mejora</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="prioridad">Prioridad</Label>
                <Select
                  value={formData.prioridad}
                  onValueChange={(value) => setFormData({ ...formData, prioridad: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona prioridad" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baja">Baja</SelectItem>
                    <SelectItem value="media">Media</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Empresa (si tiene varias) */}
            {empresas && empresas.length > 1 && (
              <div>
                <Label htmlFor="empresa">Empresa</Label>
                <Select
                  value={formData.empresa_id}
                  onValueChange={(value) => setFormData({ ...formData, empresa_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    {empresas.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Botones */}
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" type="button" onClick={() => navigate('/portal/tickets')}>
                Cancelar
              </Button>
              <Button type="submit" disabled={crearTicket.isPending}>
                {crearTicket.isPending ? 'Creando...' : 'Crear Ticket'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
